<template>
    <el-dialog
        :visible="visible"
        :modal-append-to-body="false"
        :close-on-click-modal="false"
        append-to-body
        class="vendor-new-import-dialog ah-detail-dialog"
        :width="parsedRows.length > 0 ? null : '400px'"
        top="10vh"
        :before-close="() => onClose()"
        :show-close="false"
        @open="onOpen"
    >
        <template slot="title">批次匯入客戶</template>

        <div class="detail-row">
            <!-- 上傳區 -->
            <div v-if="parsedRows.length === 0" class="upload-area">
                <el-button v-if="!isQR()" type="primary" class="upload-btn" @click="$refs.fileInput.click()">上傳 Excel 檔案</el-button>
                <div class="hint">請使用下方「下載匯入範例」所提供的標準格式。</div>
                <div>
                    <el-button @click="onDownloadTemplate">下載匯入範例</el-button>
                    <el-button plain @click="onClose()">取消</el-button>
                </div>
            </div>

            <!-- 預覽表格 -->
            <template v-else>
                <el-alert v-if="warningCount > 0" type="warning" :closable="false" style="margin-bottom: 12px">
                    共 {{ warningCount }} 筆資料有預檢警告，匯入時可能失敗
                </el-alert>
                <el-table :data="parsedRows" border style="width: 100%" height="300px" empty-text="沒有資料">
                    <el-table-column type="index" label="#" width="50" fixed />
                    <el-table-column label="匯入狀態" width="120" fixed>
                        <template slot-scope="{ row }">
                            <span :class="statusClass(row.importStatus)">{{ statusLabel(row.importStatus) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="預檢" width="140" show-overflow-tooltip>
                        <template slot-scope="{ row }">
                            <span v-if="row._warning" style="color: #E6A23C">{{ row._warning }}</span>
                            <span v-else style="color: #67C23A">OK</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="客戶全名" prop="_fnam" width="180" show-overflow-tooltip />
                    <el-table-column label="統一編號" prop="_bsn" width="120" />
                    <el-table-column label="聯絡窗口數" prop="_contactCount" width="100" />
                </el-table>
            </template>
        </div>

        <!-- 底部按鈕 -->
        <div class="detail-row footer right" v-if="parsedRows.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center;">
                <template v-if="importStatus === 'init'">
                    <div>
                        <el-button plain @click="onReset">重新上傳</el-button>
                        <el-button plain @click="onClose()">取消</el-button>
                    </div>
                    <div v-if="!isQR()">
                        <el-button type="primary" @click="onImport">開始匯入 {{ parsedRows.length }} 筆資料</el-button>
                    </div>
                </template>
                <template v-else>
                    <div style="flex: 0.9">
                        <el-progress :percentage="progress" :color="progress === 100 ? '#92DE56' : '#463cb2'" />
                    </div>
                    <div v-if="importStatus === 'importing'">
                        <el-button :loading="isStopping" plain @click="isStopping = true">
                            {{ isStopping ? '停止中' : '停止' }}
                        </el-button>
                    </div>
                    <div v-else>
                        <el-button type="primary" @click="onClose(true)">完成</el-button>
                    </div>
                </template>
            </div>
        </div>

        <input v-show="false" ref="fileInput" type="file" accept=".xlsx" @change="onFileChange" />
    </el-dialog>
</template>

<script>
import XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const MAX_CONTACTS = 3;

const SECTION_COLORS = { basic: 'FFE2EFDA', contact: 'FFDAE8FC' };

function buildColumnLayout() {
    const columns = [
        { header: '系統編號',       key: 'voo',                width: 14, section: 'basic', hint: '新增時不需填寫' },
        { header: '客戶全名',       key: 'fnam',               width: 40, section: 'basic', required: true },
        { header: '統一編號',       key: 'bsn',                width: 14, section: 'basic', text: true },
        { header: '聯絡地址',       key: 'adr',                width: 50, section: 'basic' },
        { header: '結帳日',         key: 'bilL_DAY',           width: 10, section: 'basic', hint: '填日期數字' },
        { header: '付款日',         key: 'soO_DAY',            width: 10, section: 'basic', hint: '填日期數字' },
        { header: '匯款帳號後五碼', key: 'accountLast5Number', width: 16, section: 'basic', text: true },
        { header: '備註',           key: 'note',               width: 40, section: 'basic' },
    ];

    for (let n = 1; n <= MAX_CONTACTS; n++) {
        columns.push(
            { header: `聯絡窗口${n}_姓名`,  key: `c${n}_nam`, width: 16, section: 'contact' },
            { header: `聯絡窗口${n}_稱謂`,  key: `c${n}_gen`, width: 12, section: 'contact', hint: '先生/小姐' },
            { header: `聯絡窗口${n}_職稱`,  key: `c${n}_job`, width: 16, section: 'contact' },
            { header: `聯絡窗口${n}_電話`,  key: `c${n}_tel`, width: 16, section: 'contact', text: true },
            { header: `聯絡窗口${n}_分機`,  key: `c${n}_ext`, width: 10, section: 'contact', text: true },
            { header: `聯絡窗口${n}_Email`, key: `c${n}_eml`, width: 26, section: 'contact' },
        );
    }
    return columns;
}

function buildHeaderText(col) {
    let text = col.required ? `*${col.header}` : col.header;
    if (col.hint) text += `\r\n(${col.hint})`;
    return text;
}

function normalizeKey(key) {
    return key.replace(/^\*|\*$/g, '').split(/\r?\n/)[0].trim();
}

export default {
    name: 'VendorNewImportDialog',
    props: {
        visible: Boolean,
    },
    data() {
        return {
            parsedRows: [],
            importStatus: 'init',
            isStopping: false,
            progress: 0,
        };
    },
    computed: {
        warningCount() {
            return this.parsedRows.filter(r => r._warning).length;
        },
    },
    methods: {
        onOpen() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.isStopping = false;
            this.progress = 0;
        },
        onReset() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.progress = 0;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
        onClose(refresh) {
            this.$emit('close', refresh);
        },

        // ── 下載匯入範例 ──────────────────────────────────────────
        async onDownloadTemplate() {
            const columns = buildColumnLayout();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('import');
            worksheet.columns = columns.map(col => ({ header: col.header, key: col.key, width: col.width }));

            // 覆寫標題列：加上 * 必填前綴和換行註解
            const headerRow = worksheet.getRow(1);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = buildHeaderText(col);
                cell.font = { bold: true, size: 10 };
                cell.alignment = { wrapText: true, vertical: 'top' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_COLORS[col.section] } };
            });
            headerRow.height = 40;

            // 範例資料
            worksheet.addRow({
                voo: '', fnam: '示範股份有限公司', bsn: '00000000', adr: '台北市中正區林森北路一段48號',
                bilL_DAY: 1, soO_DAY: '', accountLast5Number: '12345', note: '',
                c1_nam: '陳示範', c1_gen: '先生', c1_job: '經理', c1_tel: '0212344321', c1_ext: '777', c1_eml: 'test@mail.com',
            });

            // 資料列文字格式（防數字轉換）
            const textCols = columns.reduce((acc, col, idx) => { if (col.text) acc.push(idx + 1); return acc; }, []);
            worksheet.eachRow((row, rowNum) => {
                if (rowNum === 1) return;
                textCols.forEach(colNum => {
                    const cell = row.getCell(colNum);
                    if (cell.value != null && cell.value !== '') cell.numFmt = '@';
                });
            });

            worksheet.views = [{ state: 'frozen', ySplit: 1 }];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = '客戶匯入範例_新版.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        },

        // ── 檔案解析 ──────────────────────────────────────────────
        onFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            event.target.value = '';

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(e.target.result, { type: 'array' });
                    if (!wb.Sheets.import) throw new Error('找不到工作表「import」，請確認檔案為客戶匯出的 Excel');

                    const rawSheet = XLSX.utils.sheet_to_json(wb.Sheets.import, { defval: '' });
                    if (rawSheet.length === 0) throw new Error('Excel 內沒有資料列');

                    // 正規化 key：去除 * 前後綴，取 \r\n 前的第一行
                    const raw = rawSheet.map(row => {
                        const cleaned = {};
                        Object.keys(row).forEach(k => { cleaned[normalizeKey(k)] = row[k]; });
                        return cleaned;
                    });

                    this.parsedRows = raw.map((row) => {
                        const contacts = this.parseContacts(row);
                        const fnam = row['客戶全名'] || '';
                        const warning = this.preValidate(fnam);
                        return {
                            _raw: row,
                            _fnam: fnam,
                            _bsn: row['統一編號'] ? String(row['統一編號']) : '',
                            _contactCount: contacts.length,
                            _warning: warning,
                            importStatus: 'init',
                        };
                    });
                } catch (err) {
                    this.$message({ showClose: true, message: err.message || '檔案解析失敗', type: 'error' });
                }
            };
            reader.readAsArrayBuffer(file);
        },

        preValidate(fnam) {
            const warnings = [];
            if (!fnam) warnings.push('缺客戶全名');
            return warnings.length > 0 ? warnings.join('、') : '';
        },

        // ── 解析聯絡窗口 ──────────────────────────────────────────
        parseContacts(row) {
            const contacts = [];
            for (let i = 1; i <= MAX_CONTACTS; i++) {
                const nam = row[`聯絡窗口${i}_姓名`];
                if (!nam) continue;
                contacts.push({
                    nam,
                    gen: row[`聯絡窗口${i}_稱謂`] === '小姐' ? 1 : 0,
                    job: row[`聯絡窗口${i}_職稱`] || '',
                    tel: row[`聯絡窗口${i}_電話`] ? String(row[`聯絡窗口${i}_電話`]) : '',
                    ext: row[`聯絡窗口${i}_分機`] ? String(row[`聯絡窗口${i}_分機`]) : '',
                    eml: row[`聯絡窗口${i}_Email`] || '',
                    dep: '',
                });
            }
            return contacts;
        },

        // ── 組裝 API payload ──────────────────────────────────────
        buildPayload(raw) {
            const fnam = raw['客戶全名'] || '';
            if (!fnam) throw new Error('缺少客戶全名');

            const bsn = raw['統一編號'] ? String(raw['統一編號']) : '';
            const bilL_DAY = raw['結帳日'] ? parseInt(String(raw['結帳日']).replace('日', ''), 10) || 0 : 0;
            const soO_DAY = raw['付款日'] ? parseInt(String(raw['付款日']).replace('日', ''), 10) || 0 : 0;

            return {
                vdR_TYP: 'CL',
                fnam,
                cnam: fnam,
                bsn,
                bsN_NO: bsn ? 1 : 0,
                adr: raw['聯絡地址'] || '',
                bilL_DAY: bilL_DAY,
                soO_DAY: soO_DAY,
                accountLast5Number: raw['匯款帳號後五碼'] ? String(raw['匯款帳號後五碼']) : '',
                note: raw['備註'] || '',
                onf: 1,
                adR_CODE: '',
                ara: '',
                cty: '',
                voo: raw['系統編號'] ? String(raw['系統編號']) : '',
                vendorContacts: this.parseContacts(raw),
            };
        },

        // ── 執行匯入 ─────────────────────────────────────────────
        async onImport() {
            this.importStatus = 'importing';
            const total = this.parsedRows.length;
            let done = 0;

            for (const row of this.parsedRows) {
                if (this.isStopping) break;
                row.importStatus = 'importing';
                done++;
                this.progress = Math.round((done / total) * 100);

                try {
                    const payload = this.buildPayload(row._raw);
                    // eslint-disable-next-line no-await-in-loop
                    await this.$api_silent({
                        url: 'VendorManagementPage/UpsertVendorDetail',
                        data: payload,
                        method: 'POST',
                    });
                    row.importStatus = '已新增';
                } catch (err) {
                    if (err.response) {
                        row.importStatus = err.response.status === 400
                            ? '缺少欄位'
                            : (err.response.data || '伺服器錯誤');
                    } else {
                        row.importStatus = err.message || err.msg || '未知錯誤';
                    }
                }
            }

            this.importStatus = 'complete';
        },

        statusLabel(s) {
            if (s === 'init') return '---';
            if (s === 'importing') return '匯入中...';
            return s;
        },
        statusClass(s) {
            if (s === '已新增') return 'status-ok';
            if (s === 'init' || s === 'importing') return '';
            return 'status-err';
        },
    },
};
</script>

<style lang="scss" scoped>
.upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    .upload-btn {
        width: 260px;
    }
    .hint {
        font-size: 13px;
        color: #888;
        text-align: center;
    }
}
.status-ok {
    color: #44bf00;
    &::before { content: 'Ｏ'; }
}
.status-err {
    color: red;
    &::before { content: 'Ｘ '; }
}
</style>

<style lang="scss">
.vendor-new-import-dialog.ah-detail-dialog {
    .detail-row {
        padding: 40px;
    }
}
</style>
